import { useEffect, useState } from "react";
import { api, type Doc } from "../api.ts";
import { Markdown } from "../components/Markdown.tsx";
import { C, mono, sans, dateMaybeYear } from "../theme.ts";

// The docs layer, read from the phone. Thin on purpose: list, open, render.
//
// It exists so the roadmap and the session briefs are readable without opening
// a chat. It is not an editor and should not become one — the write path is
// the connector, where every change lands as a git commit naming the tool.

const folderOf = (path: string) => {
  const at = path.lastIndexOf("/");
  return at === -1 ? "" : path.slice(0, at);
};

const fileOf = (path: string) => path.slice(path.lastIndexOf("/") + 1);

export function Docs() {
  const [docs, setDocs] = useState<Doc[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.docs()
      .then((r) => live && setDocs(r.docs))
      .catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, []);

  useEffect(() => {
    if (!open) { setContent(null); return; }
    let live = true;
    setContent(null); setError(null);
    api.doc(open)
      .then((r) => live && setContent(r.content))
      .catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [open]);

  if (error && !docs) return <p style={{ color: C.alert, fontSize: 13, padding: "0 18px" }}>{error}</p>;

  if (open) {
    return (
      <div style={{ padding: "0 18px" }}>
        <button
          onClick={() => { setOpen(null); setError(null); }}
          style={{
            fontFamily: mono, fontSize: 11, color: C.inkFaint, letterSpacing: 1,
            background: "none", border: "none", padding: "0 0 12px", cursor: "pointer",
          }}
        >‹ ALL DOCUMENTS</button>

        <div style={{ fontFamily: mono, fontSize: 11, color: C.inkGhost, wordBreak: "break-all", marginBottom: 14 }}>
          {open}
        </div>

        {error && <p style={{ color: C.alert, fontSize: 13 }}>{error}</p>}
        {!content && !error && <p style={{ color: C.inkFaint, fontSize: 13 }}>Reading…</p>}
        {content && <Markdown text={content} />}
      </div>
    );
  }

  return (
    <div style={{ padding: "0 18px" }}>
      {!docs && <p style={{ color: C.inkFaint, fontSize: 13 }}>Looking…</p>}
      {docs?.length === 0 && (
        <p style={{ color: C.inkFaint, fontSize: 13 }}>Nothing in the docs layer yet.</p>
      )}
      {docs?.map((d, i) => {
        const folder = folderOf(d.path);
        const newFolder = i === 0 || folderOf(docs[i - 1].path) !== folder;
        return (
          <div key={d.path}>
            {newFolder && folder && (
              <p style={{
                fontFamily: mono, fontSize: 11, color: C.inkFaint, letterSpacing: 1,
                margin: i === 0 ? "0 0 8px" : "18px 0 8px",
              }}>{folder.toUpperCase()}</p>
            )}
            <div
              onClick={() => setOpen(d.path)}
              style={{
                background: C.card, borderRadius: 12, border: `1px solid ${C.rule}`,
                padding: "12px 14px", marginBottom: 10, cursor: "pointer", fontFamily: sans,
              }}
            >
              <div style={{ fontSize: 14, lineHeight: 1.4, wordBreak: "break-word" }}>{fileOf(d.path)}</div>
              <div style={{ fontFamily: mono, fontSize: 11, color: C.inkFaint, marginTop: 4 }}>
                {`${Math.max(1, Math.round(d.size / 1024))} kB · ${dateMaybeYear(d.modified.slice(0, 10))}`}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
